import { useEffect, useRef } from 'react';
import type { MouseEvent, PointerEvent as ReactPointerEvent } from 'react';

/**
 * How long a finger has to stay put before it counts as a hold rather than a tap.
 *
 * One number for the whole app: a message and a chat row answer the same gesture,
 * and two thresholds would make the second one feel broken to anybody who had
 * learned the first. MessageThread keeps its own wiring — it is welded to the
 * drag that puts selection handles on the words — but it takes the number here.
 */
export const HOLD_MS = 420;

/**
 * A long press, and the mouse's version of one.
 *
 * The handlers this returns are spread onto whatever should answer the gesture.
 * Three of them are the interesting ones:
 *
 * - every pointer move cancels, with no slop at all, because both surfaces this
 *   is used on scroll vertically and a press that survived a scroll would open a
 *   menu under a finger that was leaving;
 * - `onClickCapture` swallows the click that a press-and-hold leaves behind, so
 *   holding a chat row opens the menu instead of also opening the chat;
 * - `onContextMenu` is the same gesture for a mouse, where nobody holds a button
 *   down and waits.
 */
export function useHold(onHold: () => void): {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
  onPointerMove: () => void;
  onClickCapture: (event: MouseEvent) => void;
  onContextMenu: (event: MouseEvent) => void;
} {
  const timer = useRef<number | null>(null);
  /** Whether the press that is finishing is the one that opened the menu. */
  const opened = useRef(false);

  const cancel = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  // A component unmounted mid-press — a chat leaving the list under the finger —
  // must not wake up later and open a menu for a row that is gone.
  useEffect(() => cancel, []);

  return {
    onPointerDown: (event: ReactPointerEvent) => {
      cancel();
      opened.current = false;
      // A right-click arrives as a pointerdown too, and onContextMenu below is
      // already answering it; starting a timer as well would open the menu twice.
      if (event.button !== 0) return;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        opened.current = true;
        navigator.vibrate?.(10);
        onHold();
      }, HOLD_MS);
    },
    onPointerUp: cancel,
    onPointerLeave: cancel,
    onPointerCancel: cancel,
    onPointerMove: cancel,
    onClickCapture: (event: MouseEvent) => {
      if (!opened.current) return;
      event.preventDefault();
      event.stopPropagation();
      opened.current = false;
    },
    onContextMenu: (event: MouseEvent) => {
      event.preventDefault();
      cancel();
      onHold();
    },
  };
}
